package de.oevbs.kunde.kundenportal2.model.services.authentication

import de.oevbs.kunde.kundenportal2.ServiceInfo
import de.oevbs.sfa.communication.ClientDefinition
import de.oevbs.sfa.communication.ClientResponse

abstract class PartnerAuthenticationClientDefinition(origin: String) :
    ClientDefinition(ServiceInfo, origin, ServiceInfo.name) {

    override val requestTimeoutMillis: Long = 30000L

    /**
     * Endpunkt für den CRM-Verknüpfungsprozess, in dem ein User seinen Account mit einem Partner im CRM verknüpfen
     * möchte.
     * Validiert die erhaltenen Partnerdaten und verifiziert sie dann gegen das CRM. Stimmen die Daten und werden sie
     * noch nicht mit einem anderen Account verwendet, wird ein PIN-Brief generiert und per Post an den Partner
     * geschickt (via PDF-Mail an Hausdruckerei). Der User erhält außerdem die Rolle AZ_SFA_KUPLA_CRMAUTHED.
     * Der CRM-Link wird zwar in dieser Funktion bereits hergestellt, so dass der Prozess aus Sicht des CRMs abge-
     * schlossen ist - der User darf aber erst nach Eingabe des Freischaltcodes aus dem PIN-Brief im Portal auf die
     * Verträge zugreifen. Zu dieser Pin-Authentifizierung dient der pinAuthenticateUser-Endpunkt.
     *
     * Es wird auch ein Kontakt im CRM angelegt. Wenn die Kontaktanlage fehlgeschlägt, kommt ein Success mit einer
     * Warnungs-Meldung zurück.
     *
     * @param partnerAuthentication Die Partnerdaten, die der User im UI eingegeben hat und die er verknüpfen möchte.
     *
     * @return Success mit Unit bei Erfolg (ggf. mit Warnung zu Fehler bei Kontaktanlage), sonst Failure mit einer
     * ServiceMessage.
     *
     * __Mocks__
     *
     * //TODO Mocks
     *
     */
    suspend fun partnerAuthenticate(partnerAuthentication: PartnerAuthentication): ClientResponse<Unit> =
        post("/authentication/authenticate", partnerAuthentication)

    /**
     * Endpunkt für Schritt 2 des CRM-Verknüpfungsprozesses, den Pin-Authentifizierungsprozess, in dem ein User den
     * Freischaltcode aus seinem nun erhaltenen PIN-Brief eingibt, um seine Vertragsdaten im Kupo einsehen zu können.
     *
     * Validiert und verifiziert den Freischaltcode gegen die Datenbank und fügt dem User bei Erfolg die Rolle
     * AZ_SFA_KUPLA_PINAUTHED hinzu, damit dieser nun seine Partnerdaten im Portal sehen kann.
     * Es wird keine Änderung im CRM gemacht, da der Prozess dort bereits im vorherigen Schritt der Partner-
     * Authentifizierung abgeschlossen wurde. Die zusätzliche Rolle wird nur vom Kupo-Service und -UI durchgesetzt.
     *
     * @param pinAuthentication PIN, die der User im UI eingegeben hat. Die Email-Felder sind zunächst leer, werden aber
     * ggf. im zurückzugebenen Modell mit Auswahlmöglichkeiten belegt
     *
     * @return Ein [PinAuthentication]-Success bei Erfolg. Failure und eine ServiceMessage bei
     * Misserfolg. Im Erfolgsfall enthält das Modell bis zu 2 Mailadressen, eine aus dem CRM und eine aus dem
     * Account, aus denen der User im nächsten Schritt im UI eine auswählen muss, die dann in beiden Datenbanken
     * gespeichert wird (siehe auch [changeMail]).
     *
     * __Mocks__
     *
     * //TODO Mocks
     *
     */
    suspend fun pinAuthenticate(pinAuthentication: PinAuthentication): ClientResponse<PinAuthentication> =
        post("/authentication/pinauthenticate", pinAuthentication)

    /**
     * Endpunkt zum Anfordern eines erneuten PIN-Briefes für User, die bis zu 2 vorherige Briefe nicht erhalten haben.
     * Darf nur von crm-authentifizierten aber nicht pin-authentifizierten Usern ausgeführt werden.
     *
     * Ein User kann insgesamt 3 PIN-Briefe anfordern, aber nur 1 pro Tag. Ist das Limit überschritten, wird die
     * Anforderung abgelehnt.
     * Der Zähler des Users wird bei erfolgreicher Anforderung erhöht und das Anforderungsdatum auf heute gesetzt.
     * Dann wird ein neues PDF mit einer neuen PIN erzeugt und via Hausdruckerei abgeschickt. Die alte PIN verliert
     * sofort ihre Gültigkeit.
     *
     * @return
     * Erfolg:  Success mit Unit mit leerer Fehlerliste
     * Fehler:  Failure mit einer ServiceMessage
     *
     * __Mocks__
     *
     * //TODO Mocks
     */
    suspend fun sendNewPinLetter(): ClientResponse<Unit> =
        get("/authentication/pinrequest")

    /**
     * Endpunkt für den PIN-Authentifizierungsprozess, bei dem ein Benutzer die Authentifizierungs-PIN erfolgreich
     * eingegeben hat, aber unterschiedliche Mail-Adressen in Konto und CRM hat. Die Mail-Adressen wurden nach der
     * PIN-Eingabe in der Benutzeroberfläche angezeigt und der User hat nun
     * eine Auswahl getroffen. Die gewählte Mail-Adresse muss jetzt im CRM UND im Account gespeichert werden.
     *
     * @param pinAuthentication Die ausgewählte Mail via [PinAuthentication.selectedMail].
     *
     * @return
     * Erfolg: Success mit Unit
     * Fehler: Failure mit einer ServiceMessage.
     *
     * __Mocks__
     *
     * //TODO Mocks
     */
    suspend fun changeMail(pinAuthentication: PinAuthentication): ClientResponse<Unit> =
        post("/authentication/mail", pinAuthentication)
}